<script setup>
import { ref, watch, computed, toRaw } from 'vue';
import { useAlert } from 'dashboard/composables';
import SlotActionAutomationsAPI from 'dashboard/api/omni/slotActionAutomations';
import AutomationList from './AutomationList.vue';
import AutomationForm from './AutomationForm.vue';

function safeClone(value) {
  const seen = new WeakMap();
  const plainValue = v => {
    if (v === null || v === undefined) return v;
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') return v;
    if (t === 'function' || t === 'symbol') return undefined;
    if (t !== 'object') return undefined;
    if (v instanceof Date) return v.toISOString();
    if (typeof Element !== 'undefined' && v instanceof Element) return undefined;
    if (typeof Event !== 'undefined' && v instanceof Event) return undefined;
    return v;
  };
  const unwrap = v => {
    const plain = plainValue(v);
    if (plain !== v) return plain;
    return toRaw(v);
  };
  const rootRaw = unwrap(value);
  if (rootRaw === null || rootRaw === undefined) return rootRaw;
  if (typeof rootRaw !== 'object') return rootRaw;
  const root = Array.isArray(rootRaw) ? [] : {};
  seen.set(rootRaw, root);
  const stack = [{ src: rootRaw, dst: root }];
  while (stack.length) {
    const { src, dst } = stack.pop();
    const keys = Array.isArray(src) ? src.map((_, i) => i) : Object.keys(src);
    keys.forEach(key => {
      const child = unwrap(src[key]);
      if (child === undefined) return;
      if (child === null || typeof child !== 'object') {
        dst[key] = child;
        return;
      }
      if (seen.has(child)) {
        dst[key] = seen.get(child);
        return;
      }
      const container = Array.isArray(child) ? [] : {};
      seen.set(child, container);
      dst[key] = container;
      stack.push({ src: child, dst: container });
    });
  }
  return root;
}

const props = defineProps({
  slotId: { type: [Number, String], default: null },
  agentBotId: { type: Number, default: null },
  allSlots: { type: Array, default: () => [] },
  apiSample: { type: [Object, Array, String, Number, Boolean], default: null },
});

const automations = ref([]);
const loading = ref(false);
const saving = ref(false);
const busy = ref(false);
const simulation = ref([]);
const editing = ref(null);
const showForm = ref(false);
const fileInput = ref(null);

const load = async () => {
  if (!props.slotId) {
    automations.value = [];
    return;
  }
  loading.value = true;
  try {
    const { data } = await SlotActionAutomationsAPI.list(props.slotId);
    automations.value = data || [];
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  } finally {
    loading.value = false;
  }
};

watch(() => props.slotId, load, { immediate: true });

const startCreate = () => {
  editing.value = null;
  showForm.value = true;
};

const startEdit = automation => {
  editing.value = automation;
  showForm.value = true;
};

const cancelForm = () => {
  editing.value = null;
  showForm.value = false;
};

const saveAutomation = async payload => {
  saving.value = true;
  try {
    const safePayload = safeClone(payload) || {};
    if (editing.value) {
      await SlotActionAutomationsAPI.update(props.slotId, editing.value.id, safePayload);
    } else {
      await SlotActionAutomationsAPI.create(props.slotId, safePayload);
    }
    await load();
    showForm.value = false;
    editing.value = null;
    useAlert('Automação salva', 'success');
  } catch (err) {
    const msg = err?.response?.data?.errors?.join?.(', ') ||
      err?.response?.data?.error || err.message;
    useAlert(msg || 'Erro ao salvar automação', 'error');
  } finally {
    saving.value = false;
  }
};

const testAutomation = async payload => {
  if (!props.slotId) return;
  try {
    const { data } = await SlotActionAutomationsAPI.testConditions(props.slotId, {
      conditions: safeClone(payload.conditions),
      sampleResponse: safeClone(props.apiSample),
    });
    const msg = data?.matched
      ? 'Condições atenderiam (simulação)'
      : 'Condições NÃO atenderiam (simulação)';
    useAlert(msg, data?.matched ? 'success' : 'warning');
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  }
};

const toggle = async automation => {
  busy.value = true;
  try {
    await SlotActionAutomationsAPI.toggle(props.slotId, automation.id);
    await load();
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  } finally {
    busy.value = false;
  }
};

const remove = async automation => {
  if (!window.confirm(`Excluir automação "${automation.name}"?`)) return;
  busy.value = true;
  try {
    await SlotActionAutomationsAPI.delete(props.slotId, automation.id);
    await load();
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  } finally {
    busy.value = false;
  }
};

const reorder = async order => {
  busy.value = true;
  try {
    await SlotActionAutomationsAPI.reorder(props.slotId, order);
    await load();
  } finally {
    busy.value = false;
  }
};

const moveUp = async automation => {
  const ids = automations.value.map(a => a.id);
  const idx = ids.indexOf(automation.id);
  if (idx <= 0) return;
  [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
  await reorder(ids);
};

const moveDown = async automation => {
  const ids = automations.value.map(a => a.id);
  const idx = ids.indexOf(automation.id);
  if (idx === -1 || idx >= ids.length - 1) return;
  [ids[idx + 1], ids[idx]] = [ids[idx], ids[idx + 1]];
  await reorder(ids);
};

const simulate = async () => {
  if (!props.slotId) return;
  try {
    const { data } = await SlotActionAutomationsAPI.simulate(props.slotId, {
      sampleResponse: safeClone(props.apiSample),
    });
    simulation.value = data?.automations || [];
    useAlert('Simulação concluída', 'success');
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  }
};

const exportJson = async () => {
  try {
    const { data } = await SlotActionAutomationsAPI.export(props.slotId);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `slot-${props.slotId}-automations.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message, 'error');
  }
};

const importJson = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  try {
    const text = await file.text();
    const parsed = JSON.parse(text);
    const list = Array.isArray(parsed) ? parsed : parsed.automations || [];
    const replace = window.confirm('Substituir automações existentes? OK = substituir, Cancelar = anexar');
    await SlotActionAutomationsAPI.import(props.slotId, { automations: list, replace });
    await load();
    useAlert('Importação concluída', 'success');
  } catch (err) {
    useAlert(err?.response?.data?.error || err.message || 'JSON inválido', 'error');
  } finally {
    if (fileInput.value) fileInput.value.value = '';
  }
};

const hasSample = computed(() => props.apiSample !== null && props.apiSample !== undefined);
</script>

<template>
  <div class="space-y-3">
    <div class="flex flex-wrap items-center gap-2">
      <h4 class="text-sm font-medium text-slate-800 dark:text-slate-100">
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.TITLE', 'Automações da resposta') }}
      </h4>
      <span
        v-if="!hasSample"
        class="text-[11px] text-amber-600 dark:text-amber-400"
      >
        Rode "Testar API" e salve a amostra para habilitar o picker de campos da resposta.
      </span>
      <div class="flex-1" />
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50"
        @click="simulate"
      >
        <span class="i-lucide-zap w-3 h-3" />
        Simular
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50"
        @click="exportJson"
      >
        <span class="i-lucide-download w-3 h-3" />
        Exportar
      </button>
      <label
        class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50 cursor-pointer"
      >
        <span class="i-lucide-upload w-3 h-3" />
        Importar
        <input
          ref="fileInput"
          type="file"
          accept="application/json"
          class="hidden"
          @change="importJson"
        />
      </label>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-woot-500 text-white hover:bg-woot-600"
        @click="startCreate"
      >
        <span class="i-lucide-plus w-3 h-3" />
        Nova automação
      </button>
    </div>

    <div
      v-if="loading"
      class="text-xs text-slate-500 italic"
    >
      Carregando automações...
    </div>
    <AutomationList
      v-else
      :automations="automations"
      :simulation="simulation"
      :busy="busy"
      @edit="startEdit"
      @delete="remove"
      @toggle="toggle"
      @move-up="moveUp"
      @move-down="moveDown"
    />

    <AutomationForm
      v-if="showForm"
      :automation="editing"
      :api-sample="apiSample"
      :all-slots="allSlots"
      :agent-bot-id="agentBotId"
      :exclude-slot-id="slotId"
      @save="saveAutomation"
      @cancel="cancelForm"
      @test="testAutomation"
    />
  </div>
</template>
